import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  templatesAPI,
  ConsentTemplate,
  consentFormsAPI,
  ConsentFormCreate,
  dataAuthorizationAPI,
} from '../services/api';
import { patientsAPI, PatientInfo, professionalAPI, ProfessionalInfo, futronicAPI } from '../services/api';
import SignatureCapture from '../components/SignatureCapture';
import { 
  ArrowLeft,
  Search,
  Loader,
  AlertCircle,
  User,
  FileText,
  CheckCircle,
  X,
  PenTool,
  Calendar,
  Clock,
  Camera,
  Save,
  Fingerprint,
  Shield,
} from 'lucide-react';

const ConsentFillForm: React.FC = () => {
  const navigate = useNavigate();
  const { templateId } = useParams<{ templateId: string }>();
  const { user } = useAuth();
  
  // Estados del template
  const [template, setTemplate] = useState<ConsentTemplate | null>(null);
  const [loadingTemplate, setLoadingTemplate] = useState(true);
  const [templateError, setTemplateError] = useState('');
  
  // Estados del paciente
  const [documentNumber, setDocumentNumber] = useState('');
  const [searchingPatient, setSearchingPatient] = useState(false);
  const [patient, setPatient] = useState<PatientInfo | null>(null);
  const [patientError, setPatientError] = useState('');
  
  // Estados del consentimiento
  const [acceptsConsent, setAcceptsConsent] = useState<boolean | null>(null);
  const [acceptsDigital, setAcceptsDigital] = useState<boolean>(false);
  
  // Estados de firmas
  const [patientSignature, setPatientSignature] = useState<string | null>(null);
  const [responsibleSignature, setResponsibleSignature] = useState<string | null>(null);
  const [patientName, setPatientName] = useState('');
  const [patientDocument, setPatientDocument] = useState('');
  const [responsibleName, setResponsibleName] = useState('');
  const [responsibleDocument, setResponsibleDocument] = useState('');
  
  // Estado de foto del paciente
  const [patientPhoto, setPatientPhoto] = useState<string | null>(null);
  const [cameraModalOpen, setCameraModalOpen] = useState(false);
  const [videoStream, setVideoStream] = useState<MediaStream | null>(null);
  // Huella digital del paciente (opcional; solo paciente, no acompañante)
  const [patientFingerprint, setPatientFingerprint] = useState<string | null>(null);
  const [captureFingerprintLoading, setCaptureFingerprintLoading] = useState(false);
  const [fingerprintError, setFingerprintError] = useState<string | null>(null);
  
  // Estados del modal de firma
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [signatureType, setSignatureType] = useState<
    'patient' | 'responsible' | 'da_patient' | 'da_responsible' | null
  >(null);

  const [dataAuthLoading, setDataAuthLoading] = useState(false);
  const [dataAuthRequired, setDataAuthRequired] = useState(false);
  const [dataAuthComplete, setDataAuthComplete] = useState(true);
  const [dataAuthSaving, setDataAuthSaving] = useState(false);
  const [daAcceptTreatment, setDaAcceptTreatment] = useState(false);
  const [daAcceptSensitive, setDaAcceptSensitive] = useState(false);
  const [daAcceptContact, setDaAcceptContact] = useState(false);
  const [daPatientSignature, setDaPatientSignature] = useState<string | null>(null);
  const [daResponsibleSignature, setDaResponsibleSignature] = useState<string | null>(null);
  const [daResponsibleName, setDaResponsibleName] = useState('');
  const [daResponsibleDocument, setDaResponsibleDocument] = useState('');

  // Estado para guardar consentimiento
  const [saving, setSaving] = useState(false);
  
  // Estados para las nuevas variables del consentimiento
  const [rolPersona, setRolPersona] = useState('Paciente');
  const [nombreProfesional, setNombreProfesional] = useState('');
  const [rolProfesional, setRolProfesional] = useState('Médico/Odontólogo');
  
  // Estado de ubicación (necesario para obtener EPS y régimen)
  const [location, setLocation] = useState('Consulta Externa');
  
  useEffect(() => {
    if (templateId) {
      loadTemplate();
    }
    // Cargar información del profesional automáticamente solo si hay usuario autenticado
    if (user) {
      loadProfessionalInfo();
    }
    
    // Limpiar stream de video al desmontar
    return () => {
      if (videoStream) {
        videoStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [templateId, user]);
  
  // Efecto para inicializar el video cuando se abre el modal
  useEffect(() => {
    if (cameraModalOpen && videoStream) {
      const video = document.getElementById('camera-video') as HTMLVideoElement;
      if (video) {
        video.srcObject = videoStream;
      }
    }
  }, [cameraModalOpen, videoStream]);
  
  // Función para cargar información del profesional
  const loadProfessionalInfo = async () => {
    try {
      const professionalData = await professionalAPI.getCurrent();
      setNombreProfesional(professionalData.nombre_profesional || '');
      setRolProfesional(professionalData.rol_profesional || 'Médico/Odontólogo');
    } catch (err: any) {
      console.error('Error al cargar información del profesional:', err);
      // Si hay error, dejar los valores por defecto pero no mostrar error al usuario
      // El usuario puede llenar manualmente si es necesario
      setNombreProfesional('');
      setRolProfesional('Médico/Odontólogo');
    }
  };
  
  const loadTemplate = async () => {
    if (!templateId) return;
    
    setLoadingTemplate(true);
    setTemplateError('');
    try {
      const data = await templatesAPI.getById(templateId);
      setTemplate(data);
    } catch (err: any) {
      setTemplateError('Error al cargar la plantilla: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoadingTemplate(false);
    }
  };
  
  const searchPatient = async () => {
    if (!documentNumber.trim()) {
      setPatientError('Por favor, ingresa un número de documento');
      return;
    }
    
    setSearchingPatient(true);
    setPatientError('');
    setPatient(null);
    
    try {
      const patientData = await patientsAPI.getByDocument(documentNumber.trim());
      setPatient(patientData);
    } catch (err: any) {
      setPatientError('Paciente no encontrado: ' + (err.response?.data?.detail || err.message));
      setPatient(null);
    } finally {
      setSearchingPatient(false);
    }
  };
  
  const handleDocumentSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    searchPatient();
  };
  
  // Calcular si el paciente es menor de edad (<18 años)
  const isMinor = useMemo(() => {
    if (!patient?.fecha_nacimiento && !patient?.edad) return false;
    
    if (patient.edad !== undefined) {
      return patient.edad < 18;
    }
    
    if (patient.fecha_nacimiento) {
      const birthDate = new Date(patient.fecha_nacimiento);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      const dayDiff = today.getDate() - birthDate.getDate();
      
      if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
        return (age - 1) < 18;
      }
      return age < 18;
    }
    
    return false;
  }, [patient]);

  useEffect(() => {
    if (!patient?.documento) {
      setDataAuthLoading(false);
      setDataAuthRequired(false);
      setDataAuthComplete(true);
      return;
    }
    const ac = new AbortController();
    setDataAuthLoading(true);
    dataAuthorizationAPI
      .getRequirement(patient.documento, ac.signal)
      .then((res) => {
        setDataAuthRequired(!!res.required);
        setDataAuthComplete(!res.required);
      })
      .catch((err: unknown) => {
        const code = (err as { code?: string })?.code;
        const name = (err as { name?: string })?.name;
        if (code === 'ERR_CANCELED' || name === 'CanceledError' || name === 'AbortError') {
          return;
        }
        setDataAuthRequired(false);
        setDataAuthComplete(true);
      })
      .finally(() => {
        setDataAuthLoading(false);
      });
    return () => {
      ac.abort();
    };
  }, [patient?.documento]);

  // Función para reemplazar variables en el texto
  const replaceVariables = (text: string): string => {
    if (!text || !patient) return text;
    
    // Determinar el rol de la persona según si es menor de edad o no
    let rolPersonaValue = rolPersona;
    if (isMinor) {
      rolPersonaValue = 'Tutor/Acudiente';
    } else {
      rolPersonaValue = rolPersona || 'Paciente';
    }
    
    // Asegurarse de que nombreProfesional tenga el valor correcto (no el nombre del paciente)
    const nombreProfesionalValue = nombreProfesional && nombreProfesional.trim() 
      ? nombreProfesional.trim() 
      : '_________________';
    
    // Debug: Verificar valores (puedes eliminar esto después)
    console.log('🔍 Valores para reemplazo de variables:', {
      nombreProfesional: nombreProfesionalValue,
      nombrePaciente: patient.nombre,
      rolProfesional: rolProfesional
    });
    
    // Definir variables - IMPORTANTE: Orden específico para evitar que @Nombre reemplace @NombreProfesional
    // Primero las variables más largas (específicas), luego las más cortas (generales)
    const variables: Record<string, string> = {
      '@NombreProfesional': nombreProfesionalValue,  // Usar el valor del profesional, NO del paciente
      '@RolProfesional': rolProfesional || 'Médico/Odontólogo',
      '@RolPersona': rolPersonaValue,
      '@FechaNacimiento': patient.fecha_nacimiento || 'No especificado',
      '@Nombre': patient.nombre || '',  // Esta va después para no reemplazar @NombreProfesional
      '@Documento': patient.documento || '',
      '@Sexo': patient.sexo || 'No especificado',
      '@Edad': patient.edad ? `${patient.edad} años` : 'No especificado',
      '@Telefono': patient.telefono || 'No especificado',
      '@Fecha': new Date().toLocaleDateString('es-CO', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      }),
      '@Hora': new Date().toLocaleTimeString('es-CO', { 
        hour: '2-digit', 
        minute: '2-digit' 
      }),
    };
    
    let result = text;
    // Reemplazar variables - IMPORTANTE: Reemplazar primero las más largas (específicas) para evitar conflictos
    // Orden: @NombreProfesional antes que @Nombre
    const variableOrder = [
      '@NombreProfesional',
      '@RolProfesional',
      '@RolPersona',
      '@FechaNacimiento',
      '@Nombre',
      '@Documento',
      '@Sexo',
      '@Edad',
      '@Telefono',
      '@Fecha',
      '@Hora'
    ];
    
    variableOrder.forEach((variable) => {
      if (variables.hasOwnProperty(variable)) {
        const regex = new RegExp(variable.replace('@', '\\@'), 'g');
        result = result.replace(regex, variables[variable]);
      }
    });
    
    return result;
  };
  
  // Función para abrir el modal de captura de firma
  const openSignatureModal = (
    type: 'patient' | 'responsible' | 'da_patient' | 'da_responsible'
  ) => {
    setSignatureType(type);
    setSignatureModalOpen(true);
  };
  
  // Función para guardar la firma capturada
  const handleSaveSignature = (signatureDataUrl: string) => {
    if (signatureType === 'patient') {
      setPatientSignature(signatureDataUrl);
    } else if (signatureType === 'responsible') {
      setResponsibleSignature(signatureDataUrl);
    } else if (signatureType === 'da_patient') {
      setDaPatientSignature(signatureDataUrl);
    } else if (signatureType === 'da_responsible') {
      setDaResponsibleSignature(signatureDataUrl);
    }
    setSignatureModalOpen(false);
    setSignatureType(null);
  };
  
  // Función para limpiar la firma
  const clearSignature = (type: 'patient' | 'responsible') => {
    if (type === 'patient') {
      setPatientSignature(null);
      setPatientName('');
      setPatientDocument('');
    } else {
      setResponsibleSignature(null);
      setResponsibleName('');
      setResponsibleDocument('');
    }
  };
  
  // Función para abrir modal de cámara
  const openCameraModal = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user' } // Cámara frontal
      });
      setVideoStream(stream);
      setCameraModalOpen(true);
    } catch (err) {
      console.error('Error accediendo a la cámara:', err);
      alert('No se pudo acceder a la cámara. Por favor, verifica los permisos.');
    }
  };
  
  // Función para cerrar modal de cámara y detener el stream
  const closeCameraModal = () => {
    if (videoStream) {
      videoStream.getTracks().forEach(track => track.stop());
      setVideoStream(null);
    }
    setCameraModalOpen(false);
  };
  
  // Función para capturar foto
  const capturePhoto = () => {
    const video = document.getElementById('camera-video') as HTMLVideoElement;
    if (!video) return;
    
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    ctx.drawImage(video, 0, 0);
    const photoDataUrl = canvas.toDataURL('image/jpeg', 0.8);
    setPatientPhoto(photoDataUrl);
    closeCameraModal();
  };
  
  // Función para eliminar foto
  const removePhoto = () => {
    setPatientPhoto(null);
  };
  
  const handleSubmitDataAuthorization = async () => {
    if (!patient) return;
    if (!daAcceptTreatment || !daAcceptSensitive || !daAcceptContact) {
      alert('Debe marcar las tres casillas de aceptación.');
      return;
    }
    if (isMinor) {
      if (!daResponsibleSignature) {
        alert('Debe capturar la firma del acudiente o representante.');
        return;
      }
      if (!daResponsibleName.trim() || !daResponsibleDocument.trim()) {
        alert('Ingrese nombre y documento del representante.');
        return;
      }
    } else if (!daPatientSignature) {
      alert('Debe capturar la firma del paciente.');
      return;
    }
    setDataAuthSaving(true);
    try {
      await dataAuthorizationAPI.create({
        patient_data: {
          documento: patient.documento,
          nombre: patient.nombre,
          sexo: patient.sexo,
          fecha_nacimiento: patient.fecha_nacimiento,
          edad: patient.edad,
          telefono: patient.telefono,
        },
        responses: {
          accepts_treatment: true,
          accepts_sensitive_when_applicable: true,
          accepts_contact_channels: true,
        },
        signatures: {
          patient_signature: isMinor ? null : daPatientSignature,
          responsible_signature: isMinor ? daResponsibleSignature : null,
          patient_name: patient.nombre,
          patient_document: patient.documento,
          responsible_name: isMinor ? daResponsibleName.trim() : '',
          responsible_document: isMinor ? daResponsibleDocument.trim() : '',
        },
      });
      setDataAuthComplete(true);
    } catch (err: any) {
      alert(err.response?.data?.detail || err.message || 'Error al guardar la autorización.');
    } finally {
      setDataAuthSaving(false);
    }
  };

  // Función para guardar el consentimiento completo
  const handleSaveConsent = async () => {
    if (!template || !patient) {
      alert('Por favor, completa todos los campos requeridos.');
      return;
    }
    
    if (acceptsConsent === null) {
      alert('Por favor, indica si el paciente acepta o rechaza el consentimiento.');
      return;
    }
    
    if (!patientSignature && !isMinor) {
      alert('Por favor, captura la firma del paciente.');
      return;
    }
    
    if (isMinor && !responsibleSignature) {
      alert('Por favor, captura la firma del tutor/acudiente.');
      return;
    }
    
    // En caso de disentimiento, exigir firma y datos del responsable solo si es menor de edad
    if (acceptsConsent === false && isMinor) {
      if (!responsibleSignature) {
        alert('Por favor, captura la firma del responsable/acudiente.');
        return;
      }
      if (!responsibleName?.trim() || !responsibleDocument?.trim()) {
        alert('Por favor, ingresa el nombre y número de cédula del responsable.');
        return;
      }
    }
    
    try {
      setSaving(true);
      
      const formData: ConsentFormCreate = {
        template_id: template.id!,
        patient_data: {
          documento: patient.documento,
          nombre: patient.nombre,
          sexo: patient.sexo,
          fecha_nacimiento: patient.fecha_nacimiento,
          edad: patient.edad,
          telefono: patient.telefono,
        },
        patient_photo: patientPhoto,
        patient_fingerprint: patientFingerprint || undefined,
        consent_responses: {
          accepts_consent: acceptsConsent,
          accepts_digital: acceptsDigital,
          rol_persona: rolPersona,
          nombre_profesional: nombreProfesional,
          rol_profesional: rolProfesional,
        },
        signatures: {
          patient_signature: patientSignature,
          responsible_signature: responsibleSignature,
          patient_name: patientName,
          patient_document: patientDocument,
          responsible_name: responsibleName,
          responsible_document: responsibleDocument,
        }
      };
      
      await consentFormsAPI.create(formData);
      alert(acceptsConsent ? 'Consentimiento guardado exitosamente.' : 'Disentimiento guardado exitosamente.');
      navigate('/consents');
    } catch (err: any) {
      console.error('Error guardando consentimiento:', err);
      const detail = err.response?.data?.detail;
      alert(
        typeof detail === 'string'
          ? detail
          : 'Error al guardar el consentimiento. Por favor, intenta nuevamente.'
      );
    } finally {
      setSaving(false);
    }
  };
  
  // Inicializar datos del paciente cuando se encuentra
  useEffect(() => {
    if (patient) {
      setPatientName(patient.nombre || '');
      setPatientDocument(patient.documento || '');
      // Resetear estados del consentimiento
      setAcceptsConsent(null);
      setAcceptsDigital(false);
      setPatientSignature(null);
      setResponsibleSignature(null);
      setPatientFingerprint(null);
      setFingerprintError(null);
      setResponsibleName('');
      setResponsibleDocument('');
      setDaAcceptTreatment(false);
      setDaAcceptSensitive(false);
      setDaAcceptContact(false);
      setDaPatientSignature(null);
      setDaResponsibleSignature(null);
      setDaResponsibleName('');
      setDaResponsibleDocument('');
      // Establecer rol de persona según si es menor de edad
      if (isMinor) {
        setRolPersona('Tutor/Acudiente');
      } else {
        setRolPersona('Paciente');
      }
    }
  }, [patient, isMinor]);
  
  if (loadingTemplate) {
    return (
      <div className="flex min-h-[45vh] flex-col items-center justify-center py-16">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-2 border-slate-200 border-t-blue-700" />
          <p className="font-medium text-slate-600">Cargando plantilla…</p>
        </div>
      </div>
    );
  }
  
  if (templateError || !template) {
    return (
      <div className="animate-fade-in space-y-6 pb-4">
        <div className="max-w-4xl mx-auto">
          <div className="card bg-red-50 border-red-200">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-red-800 font-bold mb-2">Error</h3>
                <p className="text-red-700">{templateError || 'Plantilla no encontrada'}</p>
                <button
                  onClick={() => navigate('/consents')}
                  className="btn-secondary mt-4"
                >
                  <ArrowLeft className="w-5 h-5 mr-2" />
                  Volver a la lista
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="animate-fade-in space-y-6 pb-4">
      <div className="max-w-6xl mx-auto">
        <div className="page-hero mb-2">
          <button type="button" onClick={() => navigate('/consents')} className="btn-secondary mb-4 py-2 text-sm">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver a selección de plantilla
          </button>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-blue-700">
            Diligenciamiento
          </p>
          <h1 className="mb-2 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Llenar consentimiento</h1>
          <p className="text-base font-medium text-slate-800">{template.title}</p>
          <p className="mt-2 max-w-2xl text-sm text-slate-600">
            Complete los pasos con el paciente o acudiente. Los datos quedan registrados para auditoría y PDF.
          </p>
        </div>
        
        {/* Paso 1: Buscar paciente por documento */}
        {!patient && (
          <div className="card mb-6">
            <div className="flex items-center gap-3 mb-4">
              <User className="w-6 h-6 text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-800">
                Paso 1: Buscar Paciente
              </h2>
            </div>
            
            <form onSubmit={handleDocumentSubmit} className="space-y-4">
              <div>
                <label className="label">
                  Número de Documento
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={documentNumber}
                      onChange={(e) => setDocumentNumber(e.target.value)}
                      placeholder="Ingresa el número de documento del paciente"
                      className="input-field pl-10"
                      disabled={searchingPatient}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={searchingPatient || !documentNumber.trim()}
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {searchingPatient ? (
                      <>
                        <Loader className="w-5 h-5 mr-2 animate-spin" />
                        Buscando...
                      </>
                    ) : (
                      <>
                        <Search className="w-5 h-5 mr-2" />
                        Buscar
                      </>
                    )}
                  </button>
                </div>
              </div>
              
              {patientError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <p className="text-red-800 text-sm">{patientError}</p>
                  </div>
                </div>
              )}
            </form>
          </div>
        )}
        
        {/* Paso 2: Mostrar datos del paciente encontrado */}
        {patient && (
          <>
            <div className="card mb-6 bg-green-50 border-green-200">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h2 className="text-xl font-semibold text-gray-800 mb-3">
                      Paciente Encontrado
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Nombre Completo</p>
                        <p className="font-semibold text-gray-800">{patient.nombre}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Documento</p>
                        <p className="font-semibold text-gray-800">{patient.documento}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Sexo</p>
                        <p className="font-semibold text-gray-800">{patient.sexo || 'No especificado'}</p>
                      </div>
                      {patient.edad && (
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Edad</p>
                          <p className="font-semibold text-gray-800">{patient.edad} años</p>
                        </div>
                      )}
                      {patient.fecha_nacimiento && (
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Fecha de Nacimiento</p>
                          <p className="font-semibold text-gray-800">{patient.fecha_nacimiento}</p>
                        </div>
                      )}
                      {patient.telefono && (
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Teléfono</p>
                          <p className="font-semibold text-gray-800">{patient.telefono}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setPatient(null);
                    setDocumentNumber('');
                    setPatientError('');
                    setDaAcceptTreatment(false);
                    setDaAcceptSensitive(false);
                    setDaAcceptContact(false);
                    setDaPatientSignature(null);
                    setDaResponsibleSignature(null);
                    setDaResponsibleName('');
                    setDaResponsibleDocument('');
                    setDataAuthRequired(false);
                    setDataAuthComplete(true);
                    setDataAuthLoading(false);
                  }}
                  className="btn-secondary text-sm"
                >
                  Cambiar Paciente
                </button>
              </div>
            </div>

            {patient && dataAuthLoading && (
              <div className="card mb-6 flex items-center gap-3 border-blue-100 bg-blue-50/80 py-4">
                <Loader className="h-6 w-6 shrink-0 animate-spin text-blue-700" />
                <p className="text-sm text-slate-700">
                  Verificando si aplica autorización de datos personales (primera vez en el sistema)…
                </p>
              </div>
            )}

            {patient && !dataAuthLoading && dataAuthRequired && !dataAuthComplete && (
              <div className="card mb-6 border-slate-200 bg-white shadow-md">
                <div className="mb-4 flex items-start gap-3">
                  <Shield className="mt-0.5 h-8 w-8 shrink-0 text-blue-800" />
                  <div>
                    <h2 className="mb-1 text-xl font-bold text-slate-900">
                      Autorización de tratamiento de datos personales
                    </h2>
                    <p className="text-sm text-slate-600">
                      Es la primera vez que este paciente registra un consentimiento informado en esta
                      aplicación. Debe firmar esta autorización (Ley 1581 de 2012 y normativa aplicable)
                      antes de continuar con el procedimiento clínico.
                    </p>
                  </div>
                </div>
                <div className="mb-4 space-y-3 rounded-xl border border-slate-100 bg-slate-50/80 p-4 text-sm text-slate-700">
                  <p className="font-semibold text-slate-800">Finalidades principales</p>
                  <ul className="list-inside list-disc space-y-1">
                    <li>Prestación de servicios de salud e historia clínica.</li>
                    <li>Gestión con aseguradoras y el sistema de seguridad social en salud.</li>
                    <li>Reportes ante autoridades y auditoría institucional.</li>
                  </ul>
                  <p className="pt-2 text-xs text-slate-500">
                    El PDF generado incluirá membrete, logo y texto completo para archivo institucional.
                  </p>
                </div>
                <div className="space-y-3 border-t border-slate-100 pt-4">
                  <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 bg-white p-3">
                    <input
                      type="checkbox"
                      checked={daAcceptTreatment}
                      onChange={(e) => setDaAcceptTreatment(e.target.checked)}
                      className="mt-1 h-4 w-4"
                    />
                    <span>
                      Autorizo el tratamiento de mis datos personales conforme a las finalidades
                      descritas y la política de la institución.
                    </span>
                  </label>
                  <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 bg-white p-3">
                    <input
                      type="checkbox"
                      checked={daAcceptSensitive}
                      onChange={(e) => setDaAcceptSensitive(e.target.checked)}
                      className="mt-1 h-4 w-4"
                    />
                    <span>
                      En caso de datos sensibles, autorizo su tratamiento cuando sea necesario para la
                      asistencia en salud.
                    </span>
                  </label>
                  <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 bg-white p-3">
                    <input
                      type="checkbox"
                      checked={daAcceptContact}
                      onChange={(e) => setDaAcceptContact(e.target.checked)}
                      className="mt-1 h-4 w-4"
                    />
                    <span>
                      Autorizo el uso de los datos de contacto informados para fines relacionados con mi
                      asistencia.
                    </span>
                  </label>
                </div>
                <div className="mt-6 space-y-4 border-t border-slate-100 pt-4">
                  {!isMinor ? (
                    <div>
                      <p className="label">Firma del paciente (titular)</p>
                      <div className="flex flex-wrap items-center gap-3">
                        {daPatientSignature ? (
                          <img
                            src={daPatientSignature}
                            alt="Firma"
                            className="max-h-24 rounded border border-slate-200 bg-white"
                          />
                        ) : null}
                        <button
                          type="button"
                          onClick={() => openSignatureModal('da_patient')}
                          className="btn-secondary py-2 text-sm"
                        >
                          <PenTool className="mr-2 inline h-4 w-4" />
                          {daPatientSignature ? 'Volver a firmar' : 'Capturar firma'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <label className="label">Nombre del acudiente o representante *</label>
                        <input
                          type="text"
                          value={daResponsibleName}
                          onChange={(e) => setDaResponsibleName(e.target.value)}
                          className="input-field"
                          placeholder="Nombre completo"
                        />
                      </div>
                      <div>
                        <label className="label">Documento del representante *</label>
                        <input
                          type="text"
                          value={daResponsibleDocument}
                          onChange={(e) => setDaResponsibleDocument(e.target.value)}
                          className="input-field"
                          placeholder="Número de documento"
                        />
                      </div>
                      <div>
                        <p className="label">Firma del representante</p>
                        <div className="flex flex-wrap items-center gap-3">
                          {daResponsibleSignature ? (
                            <img
                              src={daResponsibleSignature}
                              alt="Firma representante"
                              className="max-h-24 rounded border border-slate-200 bg-white"
                            />
                          ) : null}
                          <button
                            type="button"
                            onClick={() => openSignatureModal('da_responsible')}
                            className="btn-secondary py-2 text-sm"
                          >
                            <PenTool className="mr-2 inline h-4 w-4" />
                            {daResponsibleSignature ? 'Volver a firmar' : 'Capturar firma'}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="mt-6 flex justify-end">
                  <button
                    type="button"
                    onClick={handleSubmitDataAuthorization}
                    disabled={dataAuthSaving}
                    className="btn-primary disabled:opacity-50"
                  >
                    {dataAuthSaving ? (
                      <>
                        <Loader className="mr-2 inline h-5 w-5 animate-spin" />
                        Guardando…
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 inline h-5 w-5" />
                        Registrar autorización y continuar
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
            
            {/* Campos para completar información del profesional */}
            {patient && !dataAuthLoading && (!dataAuthRequired || dataAuthComplete) && (
              <div className="card mb-6 bg-yellow-50 border-yellow-200">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-1">
                      Información del Profesional
                    </h3>
                    <p className="text-sm text-gray-600">
                      Los datos se obtienen automáticamente de la base de datos de Citisalud
                    </p>
                  </div>
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="label">
                      Nombre del Profesional que Informa *
                    </label>
                    <input
                      type="text"
                      value={nombreProfesional}
                      onChange={(e) => setNombreProfesional(e.target.value)}
                      className="input-field"
                      placeholder="Se carga automáticamente desde la base de datos"
                      required
                    />
                    {nombreProfesional && (
                      <p className="text-xs text-green-600 mt-1">
                        ✓ Obtenido de la tabla Profe
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="label">
                      Rol/Calidad del Profesional *
                    </label>
                    <input
                      type="text"
                      value={rolProfesional}
                      onChange={(e) => setRolProfesional(e.target.value)}
                      className="input-field"
                      placeholder="Se carga automáticamente desde Cargos_Profe"
                      required
                    />
                    {rolProfesional && rolProfesional !== 'Médico/Odontólogo' && (
                      <p className="text-xs text-green-600 mt-1">
                        ✓ Obtenido de la tabla Cargos_Profe
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="label">
                      Rol de la Persona (Paciente/Tutor/Representante)
                    </label>
                    <select
                      value={rolPersona}
                      onChange={(e) => setRolPersona(e.target.value)}
                      className="input-field"
                      disabled={isMinor}
                    >
                      <option value="Paciente">Paciente</option>
                      <option value="Tutor">Tutor</option>
                      <option value="Representante Legal">Representante Legal</option>
                    </select>
                    {isMinor && (
                      <p className="text-sm text-gray-600 mt-1">
                        Se estableció automáticamente como "Tutor/Acudiente" porque el paciente es menor de edad.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {patient && !dataAuthLoading && (!dataAuthRequired || dataAuthComplete) && (
              <>
            {/* Paso 2: Mostrar consentimiento completo */}
            <div className="card mb-6">
              <div className="flex items-center gap-3 mb-6">
                <FileText className="w-6 h-6 text-blue-600" />
                <h2 className="text-xl font-semibold text-gray-800">
                  Consentimiento Informado
                </h2>
              </div>
              
              {/* Encabezado del consentimiento */}
              <div className="mb-6 border-b border-gray-200 pb-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex-1">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">
                      {template.title}
                    </h1>
                  </div>
                  <div className="text-right text-sm text-gray-600">
                    <div className="space-y-1">
                      <p><strong>Tipo de Documento:</strong> {template.document_type}</p>
                      <p><strong>Subproceso:</strong> {template.subprocess}</p>
                      <p><strong>Código:</strong> {template.code}</p>
                      <p><strong>Página:</strong> {template.page_number}</p>
                      <p><strong>Revisión:</strong> {template.revision_date || 'N/A'}</p>
                      <p><strong>Versión:</strong> {template.version}</p>
                    </div>
                  </div>
                </div>
                <div className="text-sm text-gray-600">
                  <p><strong>Institución:</strong> {template.hospital_name}</p>
                  <p><strong>NIT:</strong> {template.hospital_nit}</p>
                </div>
              </div>
              
              {/* Datos del paciente (solo lectura) */}
              <div className="mb-6 bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-800 mb-3">Datos del Paciente</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Documento:</p>
                    <p className="font-semibold">{patient.documento}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Nombre:</p>
                    <p className="font-semibold">{patient.nombre}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Sexo:</p>
                    <p className="font-semibold">{patient.sexo || 'No especificado'}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Edad:</p>
                    <p className="font-semibold">{patient.edad ? `${patient.edad} años` : 'N/A'}</p>
                  </div>
                  {patient.fecha_nacimiento && (
                    <div>
                      <p className="text-gray-600">Fecha de Nacimiento:</p>
                      <p className="font-semibold">{patient.fecha_nacimiento}</p>
                    </div>
                  )}
                  {patient.telefono && (
                    <div>
                      <p className="text-gray-600">Teléfono:</p>
                      <p className="font-semibold">{patient.telefono}</p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Texto del consentimiento con variables reemplazadas */}
              <div className="mb-6">
                <div className="prose max-w-none whitespace-pre-wrap text-gray-800 leading-relaxed">
                  {replaceVariables(template.consent_text)}
                </div>
              </div>
              
              {/* Sección de descripción del procedimiento */}
              {template.procedure_title && (
                <div className="mb-6 border-t border-gray-200 pt-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    {template.procedure_title}
                  </h3>
                  
                  {template.procedure_description && (
                    <div className="mb-4">
                      <h4 className="font-semibold text-gray-800 mb-2">Descripción del Procedimiento:</h4>
                      <p className="text-gray-700 whitespace-pre-wrap">{template.procedure_description}</p>
                    </div>
                  )}
                  
                  {template.benefits && (
                    <div className="mb-4">
                      <h4 className="font-semibold text-gray-800 mb-2">Beneficios:</h4>
                      <p className="text-gray-700 whitespace-pre-wrap">{template.benefits}</p>
                    </div>
                  )}
                  
                  {template.risks_complications && (
                    <div className="mb-4">
                      <h4 className="font-semibold text-gray-800 mb-2">Riesgos y/o Complicaciones:</h4>
                      <p className="text-gray-700 whitespace-pre-wrap">{template.risks_complications}</p>
                    </div>
                  )}
                  
                  {template.alternatives && (
                    <div className="mb-4">
                      <h4 className="font-semibold text-gray-800 mb-2">Alternativas:</h4>
                      <p className="text-gray-700 whitespace-pre-wrap">{template.alternatives}</p>
                    </div>
                  )}
                  
                  {template.implications && (
                    <div className="mb-4">
                      <h4 className="font-semibold text-gray-800 mb-2">Implicaciones:</h4>
                      <p className="text-gray-700 whitespace-pre-wrap">{template.implications}</p>
                    </div>
                  )}
                  
                  {template.recommendations && (
                    <div className="mb-4">
                      <h4 className="font-semibold text-gray-800 mb-2">Recomendaciones:</h4>
                      <p className="text-gray-700 whitespace-pre-wrap">{template.recommendations}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Paso 3: Aceptación/Declinación del Consentimiento */}
            {acceptsConsent === null && (
              <div className="card mb-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">
                  ¿Acepta el Consentimiento Informado?
                </h2>
                
                <div className="space-y-4">
                  <div className="flex gap-4">
                    <button
                      onClick={() => setAcceptsConsent(true)}
                      className="flex-1 btn-primary py-4 text-lg"
                    >
                      <CheckCircle className="w-6 h-6 mr-2 inline" />
                      SÍ, ACEPTO
                    </button>
                    <button
                      onClick={() => setAcceptsConsent(false)}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-4 px-6 rounded-lg transition-colors text-lg"
                    >
                      <X className="w-6 h-6 mr-2 inline" />
                      NO ACEPTO
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Si acepta el consentimiento */}
            {acceptsConsent === true && (
              <div className="space-y-6">
                {/* Texto de aceptación del consentimiento */}
                <div className="card bg-green-50 border-green-200">
                  <div className="flex items-start gap-3 mb-4">
                    <CheckCircle className="w-6 h-6 text-green-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-800 mb-2">Aceptación del Consentimiento</h3>
                      <p className="text-gray-700 whitespace-pre-wrap">
                        {template.consent_acceptance_text || 'Comprendo y acepto las implicaciones del presente consentimiento informado.'}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Aceptación del diligenciamiento digital */}
                <div className="card">
                  <h3 className="font-semibold text-gray-800 mb-4">Diligenciamiento Digital</h3>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={acceptsDigital}
                      onChange={(e) => setAcceptsDigital(e.target.checked)}
                      className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <div className="flex-1">
                      <p className="text-gray-700 whitespace-pre-wrap">
                        {template.digital_authorization_text || 'Autorizo, igualmente a diligenciamiento digital del presente consentimiento'}
                      </p>
                    </div>
                  </label>
                </div>
                
                {/* Sección de Foto del Paciente */}
                <div className="card">
                  <h3 className="text-xl font-semibold text-gray-800 mb-6">Foto del Paciente</h3>
                  
                  <div className="space-y-4">
                    {patientPhoto ? (
                      <div className="relative inline-block">
                        <img 
                          src={patientPhoto} 
                          alt="Foto del paciente" 
                          className="max-w-md rounded-lg border-2 border-gray-300"
                        />
                        <button
                          onClick={removePhoto}
                          className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-2 hover:bg-red-600 shadow-lg"
                          title="Eliminar foto"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    ) : (
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 bg-gray-50 text-center">
                        <Camera className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-600 mb-4">No hay foto capturada</p>
                        <button
                          onClick={openCameraModal}
                          className="btn-primary flex items-center gap-2 mx-auto"
                        >
                          <Camera className="w-5 h-5" />
                          Tomar Foto del Paciente
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Huella digital del paciente (opcional; solo paciente, no acompañante) */}
                <div className="card">
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Huella digital del Paciente <span className="text-sm font-normal text-gray-500">(opcional)</span></h3>
                  <p className="text-sm text-gray-600 mb-4">Solo aplica al paciente. No es necesario para acompañante o responsable.</p>
                  <div className="space-y-4">
                    {fingerprintError && (
                      <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                        <AlertCircle className="w-5 h-5 flex-shrink-0" />
                        {fingerprintError}
                      </div>
                    )}
                    {patientFingerprint ? (
                      <div className="relative inline-block">
                        <img
                          src={patientFingerprint}
                          alt="Huella del paciente"
                          className="max-w-[200px] rounded-lg border-2 border-gray-300"
                        />
                        <button
                          type="button"
                          onClick={() => { setPatientFingerprint(null); setFingerprintError(null); }}
                          className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-2 hover:bg-red-600 shadow-lg"
                          title="Quitar huella"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    ) : (
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 bg-gray-50 text-center">
                        <Fingerprint className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-gray-600 mb-3 text-sm">Mantenga el dedo en el lector y pulse el botón para capturar.</p>
                        <button
                          type="button"
                          onClick={async () => {
                            setFingerprintError(null);
                            setCaptureFingerprintLoading(true);
                            try {
                              const result = await futronicAPI.captureImage();
                              if (result.success && result.image) {
                                setPatientFingerprint(result.image);
                              } else {
                                setFingerprintError(result.message || 'Error al capturar huella');
                              }
                            } catch (err: any) {
                              setFingerprintError(err.response?.data?.detail || err.message || 'Error al capturar huella');
                            } finally {
                              setCaptureFingerprintLoading(false);
                            }
                          }}
                          disabled={captureFingerprintLoading}
                          className="btn-secondary flex items-center gap-2 mx-auto disabled:opacity-50"
                        >
                          {captureFingerprintLoading ? (
                            <>
                              <Loader className="w-5 h-5 animate-spin" />
                              Capturando...
                            </>
                          ) : (
                            <>
                              <Fingerprint className="w-5 h-5" />
                              Capturar huella del paciente
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Sección de Firmas */}
                <div className="card">
                  <h3 className="text-xl font-semibold text-gray-800 mb-6">Firmas</h3>
                  
                  {/* Firma del Paciente (solo si es mayor de edad) */}
                  {!isMinor && (
                    <div className="mb-6 pb-6 border-b border-gray-200">
                      <h4 className="font-semibold text-gray-800 mb-4">Firma del Paciente</h4>
                      
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="label">Nombre del Paciente *</label>
                            <input
                              type="text"
                              value={patientName}
                              onChange={(e) => setPatientName(e.target.value)}
                              className="input-field"
                              required
                              readOnly
                            />
                          </div>
                          <div>
                            <label className="label">Número de Cédula *</label>
                            <input
                              type="text"
                              value={patientDocument}
                              onChange={(e) => setPatientDocument(e.target.value)}
                              className="input-field"
                              required
                              readOnly
                            />
                          </div>
                        </div>
                        
                        <div>
                          <label className="label">Firma del Paciente *</label>
                          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 bg-gray-50 min-h-[200px] flex items-center justify-center">
                            {patientSignature ? (
                              <div className="relative w-full">
                                <img 
                                  src={patientSignature} 
                                  alt="Firma del paciente" 
                                  className="max-w-full max-h-48 mx-auto"
                                />
                                <button
                                  onClick={() => clearSignature('patient')}
                                  className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-2 hover:bg-red-600"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => openSignatureModal('patient')}
                                className="btn-primary flex items-center gap-2"
                              >
                                <PenTool className="w-5 h-5" />
                                Capturar Firma del Paciente
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Firma del Responsable */}
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-4">
                      Firma del {isMinor ? 'Responsable/Acudiente' : 'Responsable/Acompañante'}
                    </h4>
                    
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="label">Nombre del Responsable *</label>
                          <input
                            type="text"
                            value={responsibleName}
                            onChange={(e) => setResponsibleName(e.target.value)}
                            className="input-field"
                            required
                            placeholder="Ingrese el nombre completo"
                          />
                        </div>
                        <div>
                          <label className="label">Número de Cédula del Responsable *</label>
                          <input
                            type="text"
                            value={responsibleDocument}
                            onChange={(e) => setResponsibleDocument(e.target.value)}
                            className="input-field"
                            required
                            placeholder="Ingrese el número de documento"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="label">Firma del Responsable *</label>
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 bg-gray-50 min-h-[200px] flex items-center justify-center">
                          {responsibleSignature ? (
                            <div className="relative w-full">
                              <img 
                                src={responsibleSignature} 
                                alt="Firma del responsable" 
                                className="max-w-full max-h-48 mx-auto"
                              />
                              <button
                                onClick={() => clearSignature('responsible')}
                                className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-2 hover:bg-red-600"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => openSignatureModal('responsible')}
                              className="btn-primary flex items-center gap-2"
                            >
                              <PenTool className="w-5 h-5" />
                              Capturar Firma del Responsable
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {isMinor && (
                    <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-sm text-blue-800">
                        <strong>Nota:</strong> El paciente es menor de edad ({patient.edad} años), por lo tanto solo se requiere la firma del responsable/acudiente.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Si NO acepta el consentimiento */}
            {acceptsConsent === false && (
              <div className="space-y-6">
                {/* Texto de disentimiento */}
                <div className="card bg-red-50 border-red-200">
                  <div className="flex items-start gap-3 mb-4">
                    <AlertCircle className="w-6 h-6 text-red-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-800 mb-2">Disentimiento</h3>
                      <p className="text-gray-700 whitespace-pre-wrap">
                        {template.revocation_text || 'El paciente ha manifestado su decisión de no aceptar el presente consentimiento informado.'}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Sección de Firmas para Disentimiento */}
                <div className="card">
                  <h3 className="text-xl font-semibold text-gray-800 mb-6">Firmas</h3>
                  
                  {/* Firma del Paciente (solo si es mayor de edad) */}
                  {!isMinor && (
                    <div className="mb-6 pb-6 border-b border-gray-200">
                      <h4 className="font-semibold text-gray-800 mb-4">Firma del Paciente</h4>
                      
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="label">Nombre del Paciente *</label>
                            <input
                              type="text"
                              value={patientName}
                              onChange={(e) => setPatientName(e.target.value)}
                              className="input-field"
                              required
                              readOnly
                            />
                          </div>
                          <div>
                            <label className="label">Número de Cédula *</label>
                            <input
                              type="text"
                              value={patientDocument}
                              onChange={(e) => setPatientDocument(e.target.value)}
                              className="input-field"
                              required
                              readOnly
                            />
                          </div>
                        </div>
                        
                        <div>
                          <label className="label">Firma del Paciente *</label>
                          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 bg-gray-50 min-h-[200px] flex items-center justify-center">
                            {patientSignature ? (
                              <div className="relative w-full">
                                <img 
                                  src={patientSignature} 
                                  alt="Firma del paciente" 
                                  className="max-w-full max-h-48 mx-auto"
                                />
                                <button
                                  onClick={() => clearSignature('patient')}
                                  className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-2 hover:bg-red-600"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => openSignatureModal('patient')}
                                className="btn-primary flex items-center gap-2"
                              >
                                <PenTool className="w-5 h-5" />
                                Capturar Firma del Paciente
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Firma del Responsable */}
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-4">
                      Firma del {isMinor ? 'Responsable/Acudiente' : 'Responsable/Acompañante'}
                    </h4>
                    
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="label">Nombre del Responsable *</label>
                          <input
                            type="text"
                            value={responsibleName}
                            onChange={(e) => setResponsibleName(e.target.value)}
                            className="input-field"
                            required
                            placeholder="Ingrese el nombre completo"
                          />
                        </div>
                        <div>
                          <label className="label">Número de Cédula del Responsable *</label>
                          <input
                            type="text"
                            value={responsibleDocument}
                            onChange={(e) => setResponsibleDocument(e.target.value)}
                            className="input-field"
                            required
                            placeholder="Ingrese el número de documento"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="label">Firma del Responsable *</label>
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 bg-gray-50 min-h-[200px] flex items-center justify-center">
                          {responsibleSignature ? (
                            <div className="relative w-full">
                              <img 
                                src={responsibleSignature} 
                                alt="Firma del responsable" 
                                className="max-w-full max-h-48 mx-auto"
                              />
                              <button
                                onClick={() => clearSignature('responsible')}
                                className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-2 hover:bg-red-600"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => openSignatureModal('responsible')}
                              className="btn-primary flex items-center gap-2"
                            >
                              <PenTool className="w-5 h-5" />
                              Capturar Firma del Responsable
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {isMinor && (
                    <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-sm text-blue-800">
                        <strong>Nota:</strong> El paciente es menor de edad ({patient.edad} años), por lo tanto solo se requiere la firma del responsable/acudiente.
                      </p>
                    </div>
                  )}
                </div>
                
                {/* Botón para guardar disentimiento */}
                <div className="mt-6 card bg-red-50 border-red-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-800 mb-1">Guardar Disentimiento</h3>
                      <p className="text-sm text-gray-600">
                        Guarda el disentimiento con las firmas del paciente y del responsable.
                      </p>
                    </div>
                    <button
                      onClick={handleSaveConsent}
                      disabled={saving}
                      className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed bg-red-600 hover:bg-red-700"
                    >
                      {saving ? (
                        <>
                          <Loader className="w-5 h-5 animate-spin" />
                          Guardando...
                        </>
                      ) : (
                        <>
                          <Save className="w-5 h-5" />
                          Guardar Disentimiento
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Modal de Cámara */}
            {cameraModalOpen && (
              <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-lg max-w-2xl w-full p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold text-gray-800">Tomar Foto del Paciente</h3>
                    <button
                      onClick={closeCameraModal}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <X className="w-6 h-6" />
                    </button>
                  </div>
                  
                  <div className="mb-4">
                    <video
                      id="camera-video"
                      autoPlay
                      playsInline
                      className="w-full rounded-lg"
                      style={{ maxHeight: '500px', objectFit: 'contain' }}
                    />
                  </div>
                  
                  <div className="flex gap-4 justify-end">
                    <button
                      onClick={closeCameraModal}
                      className="btn-secondary"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={capturePhoto}
                      className="btn-primary flex items-center gap-2"
                    >
                      <Camera className="w-5 h-5" />
                      Capturar Foto
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Botón para guardar consentimiento */}
            {acceptsConsent === true && (
              <div className="mt-6 card bg-blue-50 border-blue-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-1">Guardar Consentimiento</h3>
                    <p className="text-sm text-gray-600">
                      Guarda el consentimiento completo con todas las firmas y la foto del paciente.
                    </p>
                  </div>
                  <button
                    onClick={handleSaveConsent}
                    disabled={saving}
                    className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? (
                      <>
                        <Loader className="w-5 h-5 animate-spin" />
                        Guardando...
                      </>
                    ) : (
                      <>
                        <Save className="w-5 h-5" />
                        Guardar Consentimiento
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
              </>
            )}

            {/* Modal de firma: fuera del bloque del consentimiento para que exista también en autorización de datos */}
            <SignatureCapture
              isOpen={signatureModalOpen}
              onClose={() => {
                setSignatureModalOpen(false);
                setSignatureType(null);
              }}
              onSave={handleSaveSignature}
              title={
                signatureType === 'patient'
                  ? 'Capturar firma del paciente'
                  : signatureType === 'responsible'
                    ? 'Capturar firma del responsable'
                    : signatureType === 'da_patient'
                      ? 'Firma del titular (autorización de datos)'
                      : signatureType === 'da_responsible'
                        ? 'Firma del representante (autorización de datos)'
                        : 'Capturar firma'
              }
            />
          </>
        )}
      </div>
    </div>
  );
};

export default ConsentFillForm;
